// src/components/Modal.js
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import '../css/Modal.css';

/**
 * @param {ReactNode} props.header - Fast rubrikrad ovanför scrollytan. Ersätter
 *   det inbyggda krysset, som annars ligger absolutpositionerat ovanpå
 *   innehållet. Rubriken följer inte med när innehållet scrollas.
 * @param {boolean} props.hideCloseButton - Dölj det inbyggda krysset utan att
 *   lägga till en rubrikrad.
 */
function Modal({ children, isOpen, onClose, size = 'default', header = null, hideCloseButton = false }) {
  // Förhindra scrollning på body när modalen är öppen
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Spåra om mus-NEDtryckningen skedde på själva overlayn. Då stänger vi bara om
  // BÅDE mousedown och click skedde på overlayn — annars stängs modalen felaktigt
  // när man markerar text i ett fält och råkar släppa musen utanför innehållet.
  const mouseDownOnOverlay = useRef(false);

  if (!isOpen) return null;

  // Bestäm modal-bredd baserat på size prop
  const getSizeClass = () => {
    switch(size) {
      case 'xs':
        return 'modal-content-xs';
      case 'small':
        return 'modal-content-small';
      case 'medium':
        return 'modal-content-medium';
      case 'large':
        return 'modal-content-large';
      case 'max':
        return 'modal-content-max';
      case 'fullscreen':
        return 'modal-content-fullscreen';
      default:
        return 'modal-content';
    }
  };

  // Renderas i document.body, inte där den står i trädet.
  //
  // Overlayn har z-index 1000, men z-index gäller bara inom sin egen
  // stackningskontext. Står modalen inuti ett element med t.ex. `sticky z-20`
  // — som verktygsraden i tidplanen — hamnar hela modalen på effektiv nivå 20,
  // och mobilfooterns `fixed z-40` målas då ovanpå. På mobil är modalen en
  // bottom sheet, så det som doldes var exakt knappraden längst ner:
  // "Exportera"-knappen gick inte att nå.
  //
  // En portal lyfter ut overlayn ur alla sådana kontexter. React-trädet är
  // oförändrat, så context och event-bubbling fungerar precis som förut.
  return createPortal(
    <div
      className="modal-overlay"
      onMouseDown={(e) => { mouseDownOnOverlay.current = e.target === e.currentTarget; }}
      onClick={(e) => {
        if (e.target === e.currentTarget && mouseDownOnOverlay.current) onClose();
        mouseDownOnOverlay.current = false;
      }}
    >
      <div className={`${getSizeClass()}${header ? ' modal-has-header' : ''}`}>
        {header ? (
          /* Rubriken ligger UTANFÖR scrollytan. Ett sticky-element inuti
             fäster mot containerns padding-kant, vilket lämnar en remsa högst
             upp där innehållet glider förbi ovanför rubriken. */
          <div className="modal-header">{header}</div>
        ) : !hideCloseButton && (
          <button
            className="close-button"
            onClick={onClose}
            aria-label="Stäng"
          >
            ×
          </button>
        )}
        <div className="modal-scroll-container">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default Modal;
